// Turns one resolved frame + its preceding window into the item shape
// LiveDashboardModal's EquipmentLayer already knows how to draw (see
// renderEquipmentZones / renderEquipmentBodies / EquipmentMarker in
// LiveDashboardModal/components/EquipmentLayer.jsx). Mirrors
// buildCanvasPersonnel.js's shape and staleness discipline, with one
// deliberate difference -- see the STALENESS note below.
//
// STALENESS: WHICH RULE WINS. Personnel have no self-reported status for a
// bucket they're absent from, so buildCanvasPersonnel invents one (`ghost`)
// purely from how many buckets separate the resolved frame from the
// playhead. Equipment is different: the backend already classifies every
// bucket it reports a machine in as active/stale/offline (see the contract's
// per-frame `equipment[id].status`), so a second, home-grown staleness rule
// layered on top would just be two opinions about the same fact, and they
// could disagree.
//
// The rule actually applied here: the bucket-age window (same 5-bucket cap,
// same "measured against the PLAYHEAD not frame adjacency" discipline as
// personnel -- this is the exact rule that fixed the sticky-marker-forever
// bug) decides PRESENCE only -- whether a machine is drawn at all. Once a
// bucket is found within that window, its `status` field is used completely
// unmodified -- no re-deriving "stale" from bucket age, no ghost flag. One
// rule (age) gates presence, the other (server status) gates appearance;
// they never both speak to the same question.
const bucketsAgo = (frameT, playheadMs, intervalMs) => {
  if (playheadMs == null || !intervalMs || intervalMs <= 0) return 0;
  return Math.floor((playheadMs - frameT) / intervalMs);
};

// getEquipmentFaultState (EquipmentLayer.jsx, not exported) derives the
// degraded/healthy-single-point/full-body tri-state from `item.tags`
// (configured count vs. fresh count) because that is all the LIVE endpoint
// gives it. The history contract instead puts the equivalent fact directly
// on `pose.degraded` (there is no per-tag history to report), so this
// synthesizes the minimal `tags` shape that reproduces the SAME tri-state
// through the SAME shared helper, rather than forking it:
//   - 2 body points            -> full body:  [fresh, fresh]
//   - 1 body point, degraded   -> true fault:  [fresh, stale] (configured 2, 1 lost)
//   - 1 body point, !degraded  -> healthy 1pt: [fresh]
//   - no pose (offline bucket) -> []
// These tags carry no real per-tag identity (history keeps none), so
// EquipmentPopover's TagList renders them as bare role/fresh rows -- honest
// about what we actually know, not a fabricated device list.
const deriveTags = (pose) => {
  if (!pose?.bodyPoints?.length) return [];
  if (pose.bodyPoints.length >= 2) {
    return [
      { id: "head", role: "head", fresh: true },
      { id: "tail", role: "tail", fresh: true },
    ];
  }
  if (pose.degraded) {
    return [
      { id: "a", fresh: true },
      { id: "b", fresh: false },
    ];
  }
  return [{ id: "a", fresh: true }];
};

/**
 * @param {object|null} frame - the frame resolved by resolveFrame(), or null.
 * @param {object[]} prevFrames - up to 5 frames preceding `frame` (see
 *   getPrecedingFrames), nearest first.
 * @param {object[]} roster - the frames response's `equipment` array.
 * @param {number} playheadMs
 * @param {number} intervalMs - intervalUsed * 1000.
 * @returns {object[]} one entry per roster machine seen within the trailing
 *   5-bucket window, shaped for EquipmentLayer: {id, name, symbolType,
 *   spanMeters, alertEnabled, alertRadiusMeters, status, offSite, pose, tags}.
 */
export const buildCanvasEquipment = (
  frame,
  prevFrames,
  roster,
  playheadMs = null,
  intervalMs = null,
) => {
  if (!Array.isArray(roster) || roster.length === 0) return [];

  const candidates = frame ? [frame, ...(prevFrames || [])] : [];

  const result = [];
  for (const machine of roster) {
    const key = String(machine.id);

    for (const candidate of candidates) {
      const entry = candidate.equipment?.[key];
      if (!entry) continue;

      const ago = bucketsAgo(candidate.t, playheadMs, intervalMs);
      if (ago > 5) break; // older candidates are only older still: absent.

      result.push({
        id: machine.id,
        name: machine.name,
        symbolType: machine.symbolType,
        spanMeters: machine.spanMeters,
        alertEnabled: machine.alertEnabled,
        alertRadiusMeters: machine.alertRadiusMeters,
        deleted: !!machine.deleted,
        status: entry.status || "offline",
        offSite: !!entry.offSite,
        pose: entry.pose || null,
        tags: deriveTags(entry.pose),
      });
      break;
    }
    // no candidate within 5 buckets: absent entirely, not shown.
  }
  return result;
};
